import {
    annotationCodes,
    dnaComplement,
    rnaComplement,
    DNAToRNA,
    PLACEMENTS_BELOW_ANNOTATION,
    Selector
} from "../mixins/dictionaries";

import {select} from 'd3-selection';
import {scaleLinear} from 'd3-scale';
import * as bootstrap from "bootstrap";
import {appendToast} from "../bootstrap";
import {initBootstrap, loadComponents} from "../bootstrap";

const d3 = {select, scaleLinear};

const allowedKeyboardCodes = (keyboardCode) => {
    return /^[aAcCgGtTrRyYsSbBnNmMwWvVkKhHdD]+$/.test(keyboardCode) && keyboardCode !== "Tab";
}

const cleanSearchText = (seq, inDNA = true) => {
    let validatedText;
    if (inDNA) {
        validatedText = clearCharsSeq(seq.toUpperCase()).replace(/[^ACGTRYSBNMWVKHD]/g, '')
    } else {
        validatedText = clearCharsSeq(seq.toUpperCase()).replace(/[^\\*ARNDCQEGHILKMFPSTWYVX]/g, '')
    }
    return validatedText
};

const setThreshold = (zoom) => {
    if (zoom === 20) {
        return 1
    }
    return zoom <= 12 ? 3 : 2;
}

const getAffectedSlots = (startRange, endRange, slots, sequenceLength) => {
    let listSlots = [];
    let offset = 0;

    let start = startRange;
    let end = endRange;
    if (startRange > endRange) {
        end += sequenceLength;
    }

    slots.forEach(s => {
        if (s.dna_sequence) {
            const startSlot = offset;
            const endSlot = offset + s.length;
            if (
                isInRange(startSlot, endSlot, start, end)
            ) {
                listSlots.push(s)
            }
            offset += s.length;
        }
    });

    if (startRange > endRange || endRange > sequenceLength) {
        /**
         * check if the selection overlaps start and direction selection is from left to right
         * */
        offset = 0;
        start = startRange;
        end = endRange;
        if (start > end) {
            start -= sequenceLength;
        } else if (endRange > sequenceLength) {
            start -= sequenceLength;
            end -= sequenceLength;
        }

        slots.forEach(s => {
            if (s.dna_sequence) {
                const startSlot = offset;
                const endSlot = offset + s.length;
                if (
                    isInRange(startSlot, endSlot, start, end)
                ) {
                    listSlots.push(s)
                }
                offset += s.length;
            }
        });

    }

    if (startRange < 0) {
        /**
         * check if the selection overlaps start and direction selection is from right to left
         * */
        offset = 0;
        start = startRange + sequenceLength;
        end = endRange + sequenceLength;

        slots.forEach(s => {
            if (s.dna_sequence) {
                const startSlot = offset;
                const endSlot = offset + s.length;
                if (
                    isInRange(startSlot, endSlot, start, end)
                ) {
                    listSlots.push(s)
                }
                offset += s.length;
            }
        });
    }
    return listSlots;
}

const fixValidEnd = (start, end, sequenceLength) => {
    if (end < start) {
        return end + sequenceLength
    }
    return end;
}

const wrapText = (text, containerLength, regularSizeFont, fontBoxWidth) => {
    const cutTextLength = 2;
    if (text.length * regularSizeFont >= (containerLength * fontBoxWidth)) {
        if (containerLength <= 4) {
            return "..";
        }
        const maxTextLength = fontBoxWidth >= regularSizeFont ? containerLength : (containerLength * fontBoxWidth) / regularSizeFont;
        return text.substring(0, maxTextLength - cutTextLength) + "..";
    }
    return text;
}

const getMRNATextBases = (direction, substringSequence) => {
    let finalCodes = []
    substringSequence.split("").forEach((bp) => {
        const baseCode = direction === 1 ? bp : dnaComplement[bp]
        finalCodes.push(DNAToRNA[baseCode])
    })
    return finalCodes
}

const getComplement = (bp, isRNA) => {
    const table = isRNA ? rnaComplement : dnaComplement;
    return table[bp] || bp;
}

const searchInSequence = (srcData, textToSearch, start, range, id) => {
    const txtToSearch = textToSearch.toUpperCase();
    let result = [];
    let position;
    let match;
    const startRange = start;
    const endRange = start + range;

    if (range !== srcData.length) {
        srcData = srcData.substring(start, start + range);
    }

    for (let i = 0; i < srcData.length; i++) {
        if ((position = srcData.indexOf(txtToSearch, i)) > -1) {
            match = {
                pos: start + position,
                length: textToSearch.length,
                id: id
            }

            if (match.pos >= startRange && (match.pos + match.length) <= endRange) {
                result.push(match);
            }
            i = position;
        }
    }

    return result;
}

const searchInProtein = (srcData, textToSearch, start, range, id, sequenceLength) => {
    let txtToSearch = textToSearch.toUpperCase();
    let result = [];
    let match;
    let position;

    const startRange = start;
    const endRange = start + range;

    srcData.forEach(a => {
        const start = a.start;
        const aminoSequence = id === "c" ? a.amino_sequence.split("").reverse().join("") : a.amino_sequence;

        for (let i = 0; i < aminoSequence.length; i++) {
            if ((position = aminoSequence.indexOf(txtToSearch, i)) > -1) {
                match = {
                    pos: start + (position * 3),
                    length: textToSearch.length * 3,
                    id: id
                };

                if (match.pos >= startRange && (match.pos + match.length) <= endRange) {
                    result.push(match);
                }
                i = position;
            }
        }
    })
    return result;
}

const isInRange = (start, end, leftRange, rightRange, contiguous = false) => {
    let ret = end > leftRange && end < rightRange ||
        start > leftRange && start < rightRange ||
        start <= leftRange && end >= rightRange;
    if (!ret && contiguous) {
        ret = ret || end === leftRange || start === rightRange;
    }
    return ret;
}

const isInsideArea = (start, end, leftArea, rightArea, sequenceLength) => {
    let result;

    if (leftArea >= rightArea) {
        result = isInRange(start, end, leftArea, sequenceLength) || isInRange(start, end, 0, rightArea);
    } else {
        result = isInRange(start, end, leftArea, rightArea);
    }

    /*
    * When start > end and start && end > rightArea
    * */
    if (!result && start > end) {
        return (start > rightArea && end > rightArea) || isInRange(start, end + sequenceLength, leftArea, rightArea);
    }

    /*
    * When  end > sequenceLength
    * */
    if (!result && end > sequenceLength) {
        if (leftArea >= rightArea) {
            result = isInRange(0, end - sequenceLength, 0, rightArea);
        } else {
            result = isInRange(0, end - sequenceLength, leftArea, rightArea);
        }
    }

    /*
    * When rightArea > sequenceLength (it should be only for contextmenu inside selection brush)
    * */
    if (!result && rightArea > sequenceLength) {
        return isInRange(start, end, leftArea, sequenceLength) || isInRange(start, end, 0, rightArea - sequenceLength);
    }

    return result;
}

const textSize = (text, selector, fontSize = 1) => {
    if (!d3) return;
    const container = d3.select(selector).append('svg');
    container.append('text').attr("x", -99999).attr("y", -99999).style("font-size", fontSize + "em").text(text);
    const size = container.node().getBBox();
    container.remove();
    return {width: size.width, height: size.height};
}

const linearDomain = (domainRange, areaViewSize) => {
    return d3.scaleLinear()
        .domain(domainRange)
        .range([0, areaViewSize]);
}

const maxEdges = (number, min, max) => {
    if (number < min) {
        return number + max
    }
    if (number > max) {
        return number - max
    }
    return number
}

const insertableBasePairPosition = (currentInputValuePosition, newPosition, slots) => {
    if (slots && slots.length && currentInputValuePosition !== newPosition) {
        let start = 0;
        let end = 0;

        for (let count = 0; count < slots.length; count++) {
            end = start + slots[count].length;
            if (start !== end && (currentInputValuePosition >= start && currentInputValuePosition <= end)) {
                newPosition = 1 + (newPosition === 1 ? start : end);
                break;
            }
            start = end;
        }
    }

    return newPosition;
}

const fixRangeBp = (rangeBp) => {
    if (rangeBp.start > rangeBp.end) {
        return {
            start: rangeBp.end,
            end: rangeBp.start
        }
    }
    return rangeBp
}


const fixOffsetScrollPosition = (currentPosition, maxPosition) => {
    let offsetPosition = currentPosition;
    if (offsetPosition < 0 || offsetPosition > maxPosition) {
        if (currentPosition < 0) {
            offsetPosition = 0
        } else {
            offsetPosition = maxPosition
        }
    }
    return offsetPosition
}



/**
 * Annotations
 * */
const alignBelowAnnotations = (annotations) => {
    let belowLevel = 0;
    let aboveLevel = 0;
    let maxBelowLevel = -1;
    let maxAboveLevel = -1;

    PLACEMENTS_BELOW_ANNOTATION.forEach(p => {
        const filteredAnnotations = annotations.filter(a => a.placement === p);
        filteredAnnotations.forEach(a => {
            a.level += belowLevel
            maxBelowLevel = Math.max(maxBelowLevel, a.level);
        });
        belowLevel = maxBelowLevel + 1;
    })
    const filteredAboveAnnotations = annotations.filter(a => a.placement === "above");
    filteredAboveAnnotations.forEach(a => {
        a.level += aboveLevel
        maxAboveLevel = Math.max(maxAboveLevel, a.level);
    });

    return [annotations, maxBelowLevel, maxAboveLevel];
}

const attributeAnnotations = (roleId, annotationsColors) => {
    if (roleId) {
        let attributeAnnotation = annotationsColors[roleId];
        if (!attributeAnnotation.hasOwnProperty("color")) {
            attributeAnnotation.color = "#c0c0c0";
            attributeAnnotation.name = "annotation";
        }
        return attributeAnnotation;
    }
    return {'color': "#c0c0c0", 'name': "annotation"}
}

const fixLevelAnnotations = (annotations, sequenceLength) => {
    annotations.forEach(a => {
        a.level = setLevelAnnotations(
            a.placement,
            annotations,
            a.start,
            a.end,
            sequenceLength,
            a.hasOwnProperty('amino_sequence'),
            a.direction
        );
    })
};

const mergeAnnotations = (data, editorAnnotations, protocolAnnotations, sequenceLength, hiddenAnnotations, editorAnnotation = true) => {
    if (data.hasOwnProperty('context_id') || data.hasOwnProperty('context_type')) {
        if (isEditorContext(protocolAnnotations.context, data.context_id, data.context_type)) {
            protocolAnnotations.annotations = data.annotations;
        }
    } else {
        if (data.hasOwnProperty('sequence')) {
            protocolAnnotations.annotations = [];
        }
        editorAnnotations.annotations = lineUpRowAnnotations(data, hiddenAnnotations, editorAnnotation);
    }
    const dataAnnotations = {
        annotations: [...protocolAnnotations.annotations, ...editorAnnotations.annotations]
    };
    return editorAnnotation ? setEditorAnnotations(dataAnnotations, sequenceLength, hiddenAnnotations) : setTabAnnotations(dataAnnotations, sequenceLength, hiddenAnnotations);
}

const lineUpRowAnnotations = (rawData, hiddenAnnotations, editorAnnotation = true) => {
    const annotations = [];

    if (rawData.slots) {
        let offset = 0;
        rawData.slots.forEach(s => {
            if (s.dna_sequence) {
                s.dna_sequence.annotations.forEach(t => {
                    t.slot = s.dna_sequence.name;
                    if (!t.hidden) {
                        annotations.push(
                            editorAnnotation ? setEditorAnnotation(t, offset, s, hiddenAnnotations) : setTabAnnotation(t, offset, hiddenAnnotations)
                        )
                    }
                })
                offset += s.length
            }
        })
    }

    rawData.annotations.forEach(t => {
        if (!t.hidden) {
            annotations.push(
                editorAnnotation ? setEditorAnnotation(t, 0, null, hiddenAnnotations) : setTabAnnotation(t, 0, hiddenAnnotations)
            )
        }
    })

    return annotations;
}

const setEditorAnnotations = (rawData, sequenceLength, hiddenAnnotations) => {
    const annotations = lineUpRowAnnotations(rawData, hiddenAnnotations);
    fixLevelAnnotations(annotations, sequenceLength);
    return annotations;
}

const setEditorAnnotation = (rawAnnotation, offset, slot, hiddenAnnotations) => {
    const a = Object.assign({}, rawAnnotation);
    a.start += offset;
    a.end += offset;
    if (slot) {
        a.slot = slot;
    }
    a.visible = !hiddenAnnotations.includes(a.pk);
    return a;
}

const setTabAnnotations = (rawData, sequenceLength, hiddenAnnotations) => {
    return lineUpRowAnnotations(rawData, hiddenAnnotations, false);
}

const setTabAnnotation = (rawAnnotation, offset, hiddenAnnotations) => {
    const a = Object.assign({}, rawAnnotation);
    a.start += offset;
    a.end += offset;
    a.visible = !hiddenAnnotations.includes(a.pk);
    if (a.deliverable) {
        a.tab = "deliverable"
    } else {
        if (a.amino_sequence) {
            a.tab = "cds"
        } else {
            if (a.role_id === annotationCodes.warning) {
                a.tab = "warnings"
            } else {
                a.tab = "annotations"
            }
        }
    }
    return a;
}

const isEditorContext = (protocolContext, id, type) => {
    return protocolContext.type === type && protocolContext.pk.toString() === id.toString()
}

const overlapWithOtherAnnotations = (arr, from, to, isCDS, direction, sequenceLength) => {
    let overlap = false;
    let startTmp;
    let endTmp;
    let fromTmp = from;
    let toTmp = to;

    for (let i = 0; i < arr.length; i++) {
        startTmp = arr[i].start;
        endTmp = arr[i].end;
        const doNotCheckContiguous = (isCDS && arr[i].direction === direction);

        if (from >= to) {
            toTmp = to + sequenceLength;
        }
        if (arr[i].start >= arr[i].end) {
            endTmp = arr[i].end + sequenceLength
        }

        overlap = isInRange(fromTmp, toTmp, startTmp, endTmp, !doNotCheckContiguous);

        if (!overlap && (toTmp >= sequenceLength || endTmp >= sequenceLength)) {
            if (endTmp >= sequenceLength) {
                overlap = compareAnnotationWithWrappedOriginRange(startTmp, endTmp, fromTmp + sequenceLength, doNotCheckContiguous)
            } else {
                overlap = compareAnnotationWithWrappedOriginRange(fromTmp, toTmp, startTmp + sequenceLength, doNotCheckContiguous)
            }
        }

        if (overlap) {
            break;
        }
    }
    return overlap;
}

const compareAnnotationWithWrappedOriginRange = (leftRange, rightRange, start, doNotCheckContiguous = true) => {
    if (!doNotCheckContiguous) {
        return start >= leftRange && start <= rightRange;
    }
    return start > leftRange && start < rightRange;
}


const setLevelRE = (arr, item) => {
    let newLevel = 0;
    for (let i = 0; i < arr.length; i++) {
        let _cut5 = item.cut_5;
        if ((_cut5 >= arr[i].cut_5 && _cut5 <= arr[i].cut_5 + 4) ||
            (_cut5 + 4 >= arr[i].cut_5 && _cut5 + 4 <= arr[i].cut_5 + 4)) {
            if (arr[i].level >= newLevel) {
                newLevel = arr[i].level + 1;
            }
        }
    }
    return newLevel
};

const setLevelAnnotations = (placement, arr, from, to, sequenceLength, isCDS = false, direction = 0) => {
    let currentLevel;
    for (currentLevel = 0; currentLevel < arr.length; currentLevel++) {
        let filteredArrayByLevel = arr.filter(a => {
            return a.level !== undefined && a.level === currentLevel && placement === a.placement
        });
        if (!filteredArrayByLevel.length || !overlapWithOtherAnnotations(filteredArrayByLevel, from, to, isCDS, direction, sequenceLength)) {
            break;
        }
    }
    return currentLevel;
};

const getBasePairs = (sequence, start, end) => {
    let basePairs = sequence;
    let basePairsLength = basePairs.length;

    if (start > end || end > basePairsLength) {

        if (start > end) {
            end += start > end ? basePairsLength : 0;
        }
        basePairs += basePairs.substring(0, end - basePairsLength)
    }
    return basePairs.substring(start, end);
}

const getSelectedAminoAcids = (a, start, end, sequenceLength) => {
    let aminoAcids = "";
    let count = 0;

    if (a.start > a.end && start < a.start && end < a.start) {
        start += sequenceLength
    }
    end += start > end ? sequenceLength : 0;

    aminoSequenceSplitted(a.amino_sequence, a.direction).forEach(as => {
        if ((a.start + count) >= start && (a.start + count + 3) <= end) {
            aminoAcids += as;
        }
        count += 3
    });
    if (a.direction === 2) {
        aminoAcids = [...aminoAcids].reverse().join("");
    }
    return aminoAcids;
}

const manageRestrictionDensity = (restEnzymes, sequenceLength, restrictionSteps) => {
    const groupBy = (list, keyGetter) => {
        const map = new Map();
        list.forEach((item) => {
            const key = keyGetter(item);
            if (!map.has(key)) {
                map.set(key, [item]);
            } else {
                map.get(key).push(item);
            }
        });
        return map;
    };
    return groupBy(restEnzymes, r => parseInt(r.cut_5) * (restrictionSteps / sequenceLength));
};

const rangeOverlapStart = (start, end, sequenceLength, isCursor = false) => {
    if (!isCursor && end <= start) {
        return end + sequenceLength;
    }
    return end;
}

const scaleAnnotationSize = (start, end, direction, arrowSize, fontBoxWidth, regularFontSizeWidth, sequenceLength, isSlot = false) => {
    let fixedArrowSize = isSlot ? scaleArrowSize(arrowSize, fontBoxWidth, regularFontSizeWidth) : .4;

    let fixedStart = start;
    let fixedEnd = rangeOverlapStart(start, end, sequenceLength);
    let fixedDirection = direction;

    if (direction) {
        if (direction === 1) {
            fixedStart += scaleArrowSize(1, fontBoxWidth, regularFontSizeWidth);
            fixedEnd -= isSlot ? fixedArrowSize : 0;
        } else {
            fixedStart += isSlot ? fixedArrowSize : 0
            fixedEnd -= scaleArrowSize(1, fontBoxWidth, regularFontSizeWidth);
        }

        /**
         * if after the substraction start >= end
         * */
        if (fixedStart >= fixedEnd) {
            if (direction === 1) {
                fixedArrowSize = end - start;
                fixedStart = start;
                fixedEnd = start;
            } else {
                fixedArrowSize = end - start;
                fixedStart = end;
                fixedEnd = end;
            }
        }
    } else {
        fixedArrowSize = scaleArrowSize(.5, fontBoxWidth, regularFontSizeWidth);
        fixedStart += fixedArrowSize;
        fixedEnd -= fixedArrowSize;
        fixedDirection = 0;
    }

    return [fixedStart, fixedEnd, fixedArrowSize, fixedDirection]
}

const dnaClassColor = (slot) => {
    if (slot.obsolete) {
        return "biomodule-obsolete"
    } else if (slot.public) {
        return "biomodule-public"
    } else if (slot.num_collaborators) {
        return "biomodule-shared"
    }
};

const rangeAminoSequence = (a, from, to, sequenceLength) => {
    let aminoAcids = "";
    let count = 0;

    if (a.start > a.end && from < a.start && to < a.start) {
        from += sequenceLength
    }
    to += from > to ? sequenceLength : 0;

    aminoSequenceSplitted(a.amino_sequence, a.direction).forEach(as => {
        if ((a.start + count) >= from && (a.start + count + 3) <= to) {
            aminoAcids += as;
        }
        count += 3
    });
    if (a.direction === 2) {
        aminoAcids = [...aminoAcids].reverse().join("");
    }

    return aminoAcids;
}

const normalizeBPPosition = (currentPosition, sequenceLength) => {
    if (currentPosition < 0 || currentPosition > sequenceLength) {
        return currentPosition + ((currentPosition < 0) ? sequenceLength : -sequenceLength);
    }
    return currentPosition;
}

const shadeColor = (col, amt) => {
    let usePound = false;

    if (col[0] === "#") {
        col = col.slice(1);
        usePound = true;
    }

    const num = parseInt(col, 16);

    let r = (num >> 16) + amt;

    if (r > 255) r = 255;
    else if (r < 0) r = 0;

    let b = ((num >> 8) & 0x00FF) + amt;

    if (b > 255) b = 255;
    else if (b < 0) b = 0;

    let g = (num & 0x0000FF) + amt;

    if (g > 255) g = 255;
    else if (g < 0) g = 0;

    return (usePound ? "#" : "") + (g | (b << 8) | (r << 16)).toString(16);
}

const aminoSequenceSplitted = (as, direction) => {
    return direction === 2 ? [...as].reverse() : [...as];
};

const getAminoAcidVariant = (variants, position) => {
    let variant;

    variants.forEach((v) => {
        if (v.index === position) {
            variant = v
        }
    })
    return variant;
}

const variantPosition = (annotation, currentVariant) => {
    const variantIndexes = annotation.variants.map(a => a.index);
    const aminosequenceArray = annotation.amino_sequence.split("");
    return aminosequenceArray.findIndex((codon, index) => {
        const variantPosition = annotation.start + (index * 3);
        return variantIndexes.includes(index) && variantPosition === currentVariant;
    });
}

const clearCharsSeq = (text) => {
    return text.replace(/[^a-zA-Z*]/g, '').replace(/[ ?.!/;:]/g, '');
};

const countCharacters = (sequence, chars) => {
    let counter = 0;
    chars.split("").forEach((char) => {
        counter += (sequence.match(new RegExp(char, "g")) || []).length
    })
    return counter;
}

const getRangeBases = (fromBp, endBp, sequence) => {
    if (typeof sequence !== 'string' || sequence.length === 0) {
        return '';
    }

    const start = Math.round(fromBp);
    const end = Math.round(endBp);

    let result = sequence;
    if (end > sequence.length) {
        result += sequence.substring(0, end - sequence.length);
    }

    return result.substring(start, end);
}

const getBoundsBpArea = (currentBpPosition, halfAreaWidth, sequenceLength, circular) => {
    let leftBpAreaView = currentBpPosition;
    let rightBpAreaView = currentBpPosition + (halfAreaWidth * 2) > sequenceLength ? sequenceLength : currentBpPosition + (halfAreaWidth * 2);

    if (circular) {
        leftBpAreaView = currentBpPosition - halfAreaWidth;
        rightBpAreaView = currentBpPosition + halfAreaWidth;

        if (leftBpAreaView < 0) {
            leftBpAreaView += sequenceLength;
            rightBpAreaView += sequenceLength;
        }
    }

    return [leftBpAreaView, rightBpAreaView]
}

const validInputValueEdges = (currentValue, minPosition, sequenceLength, allowBeyondEnd) => {
    const maxInsertBp = allowBeyondEnd ? sequenceLength + 1: sequenceLength;

    return currentValue >= minPosition &&
           currentValue <= maxInsertBp;
}

const thousandSeparator = (number) => {
    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".")
}

const scaleArrowSize = (arrowSize, fontBoxWidth, regularFontSize) => {
    return (regularFontSize / fontBoxWidth) - ((regularFontSize - arrowSize) / fontBoxWidth);
}

const currencyFormatter = (currency) => {
    return new Intl.NumberFormat(process.env.DJANGO_LANGUAGE_CODE || 'en-us', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2
    })
}

const customizeDictionaryString = (str, newSubstring) => {
    return str.replace('{0}', newSubstring)
}

const alignBpPosition = (currentOffsetScroll, basePairNumber) => {
    return currentOffsetScroll + basePairNumber;
}

const editorPath = (slots, mainData, rootUrl, operation) => {
    let editorUrl;
    if (operation === "view-detail") {
        // view Biomodule detail on slot
        editorUrl = mainData.dna_sequence.urls.editor;
    } else {
        if (slots === null || slots.length === 0) {
            editorUrl = rootUrl + operation + "/"
        } else {
            editorUrl = mainData.urls.view + operation + "/"
        }
    }
    return editorUrl
}

const annotationPath = (annotation, action) => {
    let annotationUrl;
    if (!annotation.hasOwnProperty('slot')) {
        annotationUrl = annotation.urls.view + action;
    } else {
        annotationUrl = annotation.slot.urls.view + annotation.urls.root + action;
    }
    return annotationUrl.replace(/\/\/+/g, '/');
}

const loadModal = (url, values, errorMessage) => {
    fetchData(url, values).then(async response => {
        if (response.ok) {
            const html = await response.text();
            const modalElement = document.querySelector('#modal');

            // Clear any existing static instance before injecting the new content
            bootstrap.Modal.getInstance(modalElement)?.dispose();

            modalElement.innerHTML = html;

            // https://htmx.org/docs/#3rd-party
            htmx.process(modalElement);

            // init bootstrap component
            initBootstrap(modalElement);
            loadComponents(modalElement);

            const modal = bootstrap.Modal.getOrCreateInstance("#modal");
            modal.show();
        } else {
            appendToast(document.querySelector(Selector.TOASTS), errorMessage);
        }
    })
}

/**
 * Generic functions
 * */
const fetchData = async (url, values) => {
    const params = new URLSearchParams(values);
    const urlWithParams = `${url}?${params.toString()}`;

    return await fetch(urlWithParams, {
        method: 'GET',
        headers: {
            "X-Requested-With": "XMLHttpRequest",
        }
    })
}

const swap = (a, b) => {
    return [b, a]
}

const metaKey = (event, $editor) => {
    if ($editor.userAgent.indexOf('Mac') !== -1) {
        return event.metaKey;
    } else {
        return event.ctrlKey;
    }
}

const groupBy = (arr, elem) => {
    return arr.reduce(function (obj, item) {
        const key = item[elem];

        if (!obj.hasOwnProperty(key)) {
            obj[key] = [];
        }
        obj[key].push(item);
        return obj;

    }, {});
};

const sortBy = (key) => {
    return (a, b) => (a[key] > b[key]) ? 1 : ((b[key] > a[key]) ? -1 : 0);
};

const truncate = (string, limit) => {
    return string.length > limit ? `${string.substr(0, limit)}..` : string;
}

const truncateAnnotationLabel = (string, containerLength, newFontSize, fontSizeBase) => {
    const containerSize = containerLength * newFontSize;
    string = truncate(string, Math.floor(containerSize / fontSizeBase) - 3);
    const stringSize = string.length * fontSizeBase;

    if (containerSize < stringSize) {
        string = ""
    }
    return string
}

const toggleClass = (arrayList, nameClass, toggle = "add") => {
    arrayList.forEach((el) => {
        if (toggle === "remove") {
            el.classList.remove(nameClass)
        } else {
            el.classList.add(nameClass)
        }
    });
}

const html = (function () {
    let reEscape = /[&<>'"]/g,
        reUnescape = /&(?:amp|#38|lt|#60|gt|#62|apos|#39|quot|#34);/g,
        oEscape = {
            "&": "&amp;",
            "<": "&lt;",
            ">": "&gt;",
            "'": "&#39;",
            "\"": "&quot;"
        },
        oUnescape = {
            "&amp;": "&",
            "&#38;": "&",
            "&lt;": "<",
            "&#60;": "<",
            "&gt;": ">",
            "&#62;": ">",
            "&apos;": "'",
            "&#39;": "'",
            "&quot;": "\"",
            "&#34;": "\""
        },
        fnEscape = (m) => {
            return oEscape[m]
        },
        fnUnescape = (m) => {
            return oUnescape[m]
        },
        replace = String.prototype.replace;

    return (Object.freeze || Object)({
        escape: function escape(s) {
            return replace.call(s, reEscape, fnEscape)
        },
        unescape: function unescape(s) {
            return replace.call(s, reUnescape, fnUnescape)
        }
    })
}());

const createVirtualElement = (clientX, clientY) => {
    return {
        getBoundingClientRect: () => ({
            width: 0,
            height: 0,
            top: clientY,
            right: clientX,
            bottom: clientY,
            left: clientX,
        }),
    };
}

const getGCContent = (start, end, sequence)  => {
    const tmpEnd = start >= end ? end + sequence?.length : end;
    const selectedBases = getRangeBases(start, tmpEnd, sequence);
    if (!selectedBases.length) return '0.00';
    return ((countCharacters(selectedBases.toUpperCase(), "CG") / selectedBases.length) * 100).toFixed(2);
}

const loadStaticModal = () => {
    let $staticModal = document.querySelector(Selector.MODAL),
        $content = $staticModal?.querySelector('.modal-content'),
        $template = document.querySelector('template#modal-static-template');

    if (!$content || !$template) return;

    // skip if the static template is already shown
    if ($content.querySelector('#reconnect-ws')) return;

    // Clear any existing static instance before injecting the new content
    bootstrap.Modal.getInstance($staticModal)?.dispose();

    $content.innerHTML = $template.innerHTML;

    const $modal = bootstrap.Modal.getOrCreateInstance($staticModal, {
        keyboard: false,
        backdrop: "static"
    });
    $modal.show();

    $content.querySelector('#reconnect-ws')
        ?.addEventListener('click', () => location.reload());
}

export {
    /**
     * editor
     * */
        allowedKeyboardCodes,
    cleanSearchText,
    setThreshold,
    getAffectedSlots,
    fixValidEnd,
    wrapText,
    getMRNATextBases,
    getComplement,
    searchInSequence,
    searchInProtein,

    isInRange,
    isInsideArea,
    textSize,
    linearDomain,
    maxEdges,

    alignBpPosition,
    insertableBasePairPosition,
    editorPath,
    annotationPath,
    loadModal,
    fixRangeBp,
    fixOffsetScrollPosition,
    getGCContent,

    /**
     * Annotations
     * */
        scaleAnnotationSize,
    alignBelowAnnotations,
    setEditorAnnotations,
    mergeAnnotations,
    attributeAnnotations,
    aminoSequenceSplitted,
    getAminoAcidVariant,
    variantPosition,
    getSelectedAminoAcids,
    rangeAminoSequence,
    truncateAnnotationLabel,

    /**
     * restriction Enzymes
     * */
        manageRestrictionDensity,
    setLevelRE,

    dnaClassColor,
    shadeColor,

    rangeOverlapStart,
    normalizeBPPosition,
    clearCharsSeq,
    countCharacters,
    getBasePairs,
    getRangeBases,
    getBoundsBpArea,
    validInputValueEdges,
    thousandSeparator,
    isEditorContext,

    /**
     * tabs
     * */
        setTabAnnotations,
    customizeDictionaryString,
    currencyFormatter,

    /**
     * test
     */
        lineUpRowAnnotations,
    compareAnnotationWithWrappedOriginRange,

    /**
     * common functions
     * */
    fetchData,
    swap,
    metaKey,
    groupBy,
    sortBy,
    truncate,
    toggleClass,
    html,
    createVirtualElement,
    loadStaticModal
}